import { useThree } from "@react-three/fiber";
import React, { useMemo, useRef, useState } from "react";
import { Easing, interpolate, useCurrentFrame, useVideoConfig } from "remotion";
import { CanvasTexture } from "three";
import { PHONE_CURVE_SEGMENTS, PhoneLayout } from "./helpers/layout";
import { roundedRect } from "./helpers/rounded-rectangle";
import { drawColon, drawSevenSegmentDigit } from "./helpers/seven-segment";
import { RoundedBox } from "./RoundedBox";
import {
  TIMER_COUNT_START_FRAME,
  TIMER_POP_UP_FRAME,
  TIMER_TRAVEL_END_FRAME,
} from "./helpers/timeline";

// Timer slides in already facing the camera at the shared flat-view
// tilt. No rotation interp because it never sat on the phone screen.
const FACE_CAMERA_X = -Math.PI / 4;

const TEXT_CANVAS_W = 1280;
const TEXT_CANVAS_H = 500;

const LIT_COLOR = "#c4f9b0";
const OFF_COLOR = "#1f2a1c";

const formatHHMMSS = (totalSeconds: number) => {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return [h, m, s].map((n) => n.toString().padStart(2, "0"));
};

const drawTimerCanvas = (
  ctx: OffscreenCanvasRenderingContext2D,
  hh: string,
  mm: string,
  ss: string,
) => {
  const W = TEXT_CANVAS_W;
  const H = TEXT_CANVAS_H;
  ctx.clearRect(0, 0, W, H);

  // Layout: 6 digit cells + 2 colon cells, evenly distributed.
  const padX = W * 0.03;
  const padY = H * 0.08;
  const digitH = H - padY * 2;
  const digitW = digitH * 0.38;
  const colonW = digitH * 0.14;
  const totalContentW = digitW * 6 + colonW * 2;
  const totalGapW = W - padX * 2 - totalContentW;
  const gap = totalGapW / 7;

  const chars = [hh[0], hh[1], ":", mm[0], mm[1], ":", ss[0], ss[1]];

  let x = padX;
  for (const ch of chars) {
    if (ch === ":") {
      drawColon(ctx, x, padY, colonW, digitH, LIT_COLOR);
      x += colonW + gap;
    } else {
      drawSevenSegmentDigit(
        ctx,
        x,
        padY,
        digitW,
        digitH,
        ch,
        LIT_COLOR,
        OFF_COLOR,
      );
      x += digitW + gap;
    }
  }
};

export const TimerPopUp: React.FC<{
  readonly phoneLayout: PhoneLayout;
  readonly worldOffset?: [number, number, number];
  // Where the timer settles after sliding in. Defaults to the scene
  // origin; PhotoParsingScene supplies a target slot beside the phone.
  readonly targetPosition?: [number, number, number];
  // Shift the internal frame clock. Use when embedding in a composition whose
  // frame 0 does not align with this scene's TIMER_POP_UP_FRAME.
  // e.g. frameOffset=76 makes the timer pop at composition frame 80.
  readonly frameOffset?: number;
}> = ({
  phoneLayout,
  targetPosition,
  frameOffset = 0,
}) => {
  const rawFrame = useCurrentFrame();
  const frame = rawFrame + frameOffset;
  const { fps } = useVideoConfig();

  const screenWidth = phoneLayout.screen.width;

  // Bezel dimensions — wide and short to match a stopwatch panel (~2.4:1).
  const bezelWidth = screenWidth * 0.84;
  const bezelHeight = bezelWidth / 2.4;
  const bezelRadius = bezelHeight * 0.18;
  const bezelDepth = bezelWidth * 0.045;

  // LCD inset — slightly smaller than bezel, sits raised on the bezel face.
  const bezelInset = bezelHeight * 0.11;
  const lcdWidth = bezelWidth - bezelInset * 2;
  const lcdHeight = bezelHeight - bezelInset * 2;
  const lcdRadius = Math.max(0, bezelRadius - bezelInset * 0.6);

  const lcdShape = useMemo(
    () => roundedRect({ width: lcdWidth, height: lcdHeight, radius: lcdRadius }),
    [lcdWidth, lcdHeight, lcdRadius],
  );

  // LED digit plane — slightly smaller than the LCD with the same aspect ratio,
  // transparent BG so the LCD's black shape provides the rounded-rect frame.
  const ledScale = 0.92;
  const ledWidth = lcdWidth * ledScale;
  const ledHeight = lcdHeight * ledScale;

  // Slide-in eased the same way the whiteboard slides — easeOutCubic over
  // the same 20-frame duration, so both entrances share the same pace.
  const travelProgress = interpolate(
    frame,
    [TIMER_POP_UP_FRAME, TIMER_TRAVEL_END_FRAME],
    [0, 1],
    {
      easing: Easing.out(Easing.cubic),
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    },
  );

  // Move-to destination — defaults to the scene origin if no target
  // supplied; PhotoParsingScene supplies the slot beside the phone where
  // the timer comes to rest.
  const targetX = targetPosition ? targetPosition[0] : 0;
  const targetYFloor = targetPosition ? targetPosition[1] : 0;
  const targetZ = targetPosition ? targetPosition[2] : bezelWidth * 0.6;
  // Lift the timer slightly above the target floor so it reads as floating
  // in the whiteboard slot rather than sinking into the ground plane.
  const targetY = targetYFloor + bezelWidth * 0.55;

  // Slide-in start: well off-screen RIGHT on the same Y/Z plane as the
  // target. Same easing + duration as the whiteboard slide-in so the
  // two reveals share pace.
  const popStartX = targetX + bezelWidth * 8;
  const popStartY = targetY;
  const popStartZ = targetZ;

  // Pure straight-line interpolation. With popStartY == targetY and
  // popStartZ == targetZ the motion is purely horizontal across X.
  const worldX = popStartX + (targetX - popStartX) * travelProgress;
  const worldY = popStartY + (targetY - popStartY) * travelProgress;
  const worldZ = popStartZ + (targetZ - popStartZ) * travelProgress;

  // Steady camera-facing rotation throughout — no interp needed because
  // the timer never sat on the phone screen.
  const rotX = FACE_CAMERA_X;
  const popScale = 1;

  // Count-up clock: elapsed seconds since TIMER_COUNT_START_FRAME, formatted
  // HH:MM:SS. Shows 00:00:00 until the count begins.
  const elapsed = Math.max(0, (frame - TIMER_COUNT_START_FRAME) / fps);
  const totalSeconds = Math.floor(elapsed);
  const [hh, mm, ss] = formatHHMMSS(totalSeconds);
  const timeKey = `${hh}:${mm}:${ss}`;

  // Stable canvas + texture; redraw only when the displayed time changes.
  const [canvas] = useState(
    () => new OffscreenCanvas(TEXT_CANVAS_W, TEXT_CANVAS_H),
  );
  const [ctx] = useState(() => {
    const c = canvas.getContext("2d");
    if (!c) throw new Error("Failed to get 2D context");
    return c;
  });
  const [texture] = useState(() => {
    const t = new CanvasTexture(canvas);
    return t;
  });
  const { invalidate } = useThree();

  const lastDrawn = useRef<string>("");
  if (lastDrawn.current !== timeKey) {
    drawTimerCanvas(ctx, hh, mm, ss);
    lastDrawn.current = timeKey;
    texture.needsUpdate = true;
    invalidate();
  }

  if (frame < TIMER_POP_UP_FRAME) return null;

  return (
    <group
      position={[worldX, worldY, worldZ]}
      rotation={[rotX, 0, 0]}
      scale={popScale}
    >
      {/* Bezel — light gray chassis */}
      <RoundedBox
        width={bezelWidth}
        height={bezelHeight}
        radius={bezelRadius}
        depth={bezelDepth}
        curveSegments={PHONE_CURVE_SEGMENTS}
        position={[-bezelWidth / 2, -bezelHeight / 2, 0]}
      >
        <meshPhongMaterial color="#d2d6da" shininess={90} specular="#ffffff" />
      </RoundedBox>

      {/* LCD black inset — flat shape sitting on the bezel front face */}
      <mesh
        position={[-lcdWidth / 2, -lcdHeight / 2, bezelDepth + 0.001]}
      >
        <shapeGeometry args={[lcdShape]} />
        <meshPhongMaterial color="#06090a" shininess={140} specular="#1a2624" />
      </mesh>

      {/* LED segments — transparent BG, only digits + colons rendered */}
      <mesh position={[0, 0, bezelDepth + 0.002]}>
        <planeGeometry args={[ledWidth, ledHeight]} />
        <meshBasicMaterial map={texture} transparent toneMapped={false} />
      </mesh>
    </group>
  );
};
